import React from "react";
import {
  Control,
  Controller,
  DeepMap,
  FieldError,
  useForm,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from "react-hook-form";
import { Calendar } from "primereact/calendar";
import { defaultErrorHandler } from "@utilities/toast";
import { Button } from "@ui/Button";
import { DateTime } from "luxon";
import classNames from "classnames";
import { Label } from "@components/ui/Input";

export type UpdateOfferWindowFormData = {
  start: Date;
  end: Date;
};

export type UpdateOfferWindowSubmitData = {
  start: Date;
  end: Date;
};

type UpdateOfferWindowFormProps = {
  defaultValues: Partial<UpdateOfferWindowFormData>;
  onSubmit: (data: UpdateOfferWindowFormData) => void | Promise<void>;
};

type DateComponentProps = {
  control: Control<UpdateOfferWindowFormData>;
  setValue: UseFormSetValue<UpdateOfferWindowFormData>;
  errors: DeepMap<UpdateOfferWindowFormData, FieldError>;
  watch: UseFormWatch<UpdateOfferWindowFormData>;
  trigger: UseFormTrigger<UpdateOfferWindowFormData>;
};

export function UpdateOfferWindowForm({
  onSubmit,
  defaultValues,
}: UpdateOfferWindowFormProps) {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    watch,
    setValue,
    trigger,
  } = useForm<UpdateOfferWindowFormData>({
    defaultValues,
  });

  const wrappedOnSubmit = async (data: UpdateOfferWindowFormData) => {
    try {
      await onSubmit(data);
    } catch (error) {
      defaultErrorHandler(error);
    }
  };

  const dateComponentProps: DateComponentProps = {
    control,
    setValue,
    errors,
    watch,
    trigger,
  };

  return (
    <form onSubmit={handleSubmit(wrappedOnSubmit)}>
      <div className="grid tablet:grid-cols-2 gap-4">
        <StartDate {...dateComponentProps} />
        <EndDate {...dateComponentProps} />
      </div>

      <Button size="lg" type="submit" disabled={isSubmitting} className="mt-5">
        Update offer window
      </Button>
    </form>
  );
}

function StartDate({
  control,
  setValue,
  errors,
  watch,
  trigger,
}: DateComponentProps) {
  const start = watch("start");
  const end = watch("end");

  return (
    <div className="mt-5">
      <Label>Start</Label>
      <div className="mt-1">
        <Controller
          name="start"
          control={control}
          rules={{
            required: "Start Date is required",
          }}
          render={({ field: { onChange, value } }) => (
            <Calendar
              value={value}
              onChange={({ value }) => onChange(value)}
              appendTo="self"
              monthNavigator
              showTime
              hourFormat="12"
              showIcon={true}
              onShow={() => {
                if (!value) {
                  setValue("start", getDefaultStartDateTime().toJSDate());
                }
              }}
              onHide={() => {
                //Set the end date to automatically be 1 day later, for convenience
                if (start && !end) {
                  const newEndDate = DateTime.fromJSDate(start).plus({
                    day: 1,
                  });
                  setValue("end", newEndDate.toJSDate());

                  //Must manually trigger re-validation of the end date field, in case
                  //setting the value causes it to transition from error state to not error state
                  trigger("end");
                }
              }}
              className={classNames({
                "border-red-300": errors.start,
              })}
            />
          )}
        />
        {errors.start && (
          <small className="text-sm text-red-600">Start date is required</small>
        )}
      </div>
    </div>
  );
}

function EndDate({ control, setValue, errors }: DateComponentProps) {
  return (
    <div className="mt-5">
      <Label>End</Label>
      <div className="mt-1">
        <Controller
          name="end"
          control={control}
          rules={{
            required: "End Date is required",
          }}
          render={({ field: { onChange, value } }) => (
            <Calendar
              value={value}
              onChange={({ value }) => onChange(value)}
              appendTo="self"
              monthNavigator
              showTime
              hourFormat="12"
              showIcon={true}
              onShow={() => {
                if (!value) {
                  setValue("end", getDefaultEndDateTime().toJSDate());
                }
              }}
              className={classNames({
                "border-red-300": errors.end,
              })}
            />
          )}
        />
        {errors.end && (
          <small className="text-sm text-red-600">End date is required</small>
        )}
      </div>
    </div>
  );
}

function getDefaultStartDateTime() {
  return DateTime.now()
    .setZone("US/Pacific")
    .set({ hour: 10, minute: 0, second: 0, millisecond: 0 });
}

function getDefaultEndDateTime() {
  return getDefaultStartDateTime().plus({ day: 1 });
}
